/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import {
  ActionFunctionMap,
  ActionObject,
  assign,
  createMachine,
  Interpreter,
  spawn,
} from 'xstate-v4'
import { Actor } from 'xstate-v4/lib/Actor'
import { Image } from '../domain/types'
import { FilestackImagesWithDimensions } from '../types'
import { UploadedImagePreviewStateMachineWithConfig } from './UploadedImagePreviewMachine'

export interface ImageUploaderContext {
  imagesPreviews: Record<string, Actor>
  campsiteSlug: string
  token: string
}

interface ImageUploaderSchema {
  states: {
    idle: {}
    fetching: {}
    uploading: {}
    verifying: {}
  }
}

export type ImageUploaderEvents =
  | { type: 'NEW_IMAGES'; filestackImages: FilestackImagesWithDimensions }
  | { type: 'REMOVE'; imagePreviewId: string }
  | { type: 'IMAGE_UPDATED' }

interface ImageUploaderMachineServices {
  uploadImages: (
    context: ImageUploaderContext,
    event: ImageUploaderEvents,
  ) => Promise<Image[]>
}

export const buildImageUploaderMachine = (
  services: ImageUploaderMachineServices,
  machine: UploadedImagePreviewStateMachineWithConfig,
  actions?:
    | ActionFunctionMap<
        ImageUploaderContext,
        ImageUploaderEvents,
        ActionObject<ImageUploaderContext, ImageUploaderEvents>
      >
    | undefined,
) => {
  return createMachine<ImageUploaderContext, ImageUploaderEvents>(
    {
      id: 'imageUploader',
      initial: 'fetching',
      on: {
        IMAGE_UPDATED: 'verifying',
        NEW_IMAGES: 'uploading',
        REMOVE: {
          actions: assign({
            imagesPreviews: ({ imagesPreviews }, { imagePreviewId }) => {
              const { [imagePreviewId]: _, ...previews } = imagesPreviews
              return previews
            },
          }),
          target: 'verifying',
        },
      },
      states: {
        idle: {},
        verifying: {
          invoke: {
            src: 'checkIfChildrenAreInCaptionsAddedState',
            onDone: 'valid',
            onError: 'invalid',
          },
        },
        fetching: {
          invoke: {
            src: 'getAllImages',
            onDone: {
              target: 'verifying',
              actions: assign({
                imagesPreviews: (
                  { imagesPreviews, campsiteSlug, token },
                  { data },
                ) => {
                  const reviewPhotos = data.reduce(
                    (ac: any, image: any) => {
                      ac[image.id] = spawn(
                        machine.withContext({
                          image,
                          campsiteSlug,
                          token,

                          caption: image.caption || '',
                          updateRepeats: 0,
                        }),
                      )
                      return ac
                    },
                    { ...imagesPreviews },
                  )
                  return reviewPhotos
                },
              }),
            },
            onError: {
              target: 'idle',
              actions: 'logError',
            },
          },
        },
        valid: {},
        invalid: {},
        uploading: {
          invoke: {
            src: 'uploadImages',
            onDone: {
              target: 'idle',
              actions: assign({
                imagesPreviews: (
                  { imagesPreviews, campsiteSlug, token },
                  { data },
                ) =>
                  data.reduce(
                    (ac: any, image: any) => {
                      ac[image.id] = spawn(
                        machine.withContext({
                          image,
                          campsiteSlug,
                          token,
                          caption: '',
                          updateRepeats: 0,
                        }),
                      )

                      return ac
                    },
                    { ...imagesPreviews },
                  ),
              }),
            },
            onError: {
              target: 'idle',
              actions: 'logError',
            },
          },
        },
      },
    },
    {
      services: {
        ...services,
        checkIfChildrenAreInCaptionsAddedState: ({ imagesPreviews }) =>
          new Promise((resolve, reject) => {
            for (const imgKey in imagesPreviews) {
              if (
                Object.prototype.hasOwnProperty.call(imagesPreviews, imgKey) &&
                !imagesPreviews[imgKey].state.matches('valid')
              ) {
                reject(
                  new Error(
                    'Image caption validation failed: not all images have valid captions',
                  ),
                )
                return
              }
            }
            resolve(true)
          }),
      },
      actions: {
        logError: () => {
          throw new Error('imageUploaderMachine logError not implemented')
        },
        ...actions,
      },
    },
  )
}

export type ImageUploaderStateMachineInterpreter = Interpreter<
  ImageUploaderContext,
  ImageUploaderSchema,
  ImageUploaderEvents
>
